/**
 * Mentor setup checklist card (founder QA F1/F2, 2026-10-16).
 *
 * Replaces the fixed mentor-workspace plan. Each step's done state comes from
 * the server, derived from the mentor's own rows, and the card refetches every
 * time its screen gains focus, so creating a mentee ticks step 1 on return.
 * Each open step has its own reason, its own success marker and a "do it now"
 * deep link to the exact place in the workspace.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusEffect, useRouter } from 'expo-router';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from '@/hooks/useTranslation';
import { trackAuthEvent } from '@/services/analytics';
import { MentorEngagementsAPI } from '@/services/api/mentorEngagementsAPI';
import UpgradeModal from '@/components/billing/UpgradeModal';
import { hasEntitlement } from '@/utils/billingAccess';
import type { MentorChecklistStep, MentorSetupChecklist } from '@/types/mentorEngagements';
import {
  CHECKLIST_STEP_ICONS,
  type ChecklistCardState,
  checklistCardState,
  checklistCompleteHref,
  checklistStepHref,
  type ChecklistHref,
} from './setupChecklist';

type Surface = 'dashboard' | 'progress' | 'mentees';

export interface MentorChecklistController {
  state: ChecklistCardState;
  load: () => Promise<void>;
}

/**
 * Loads the live checklist (refetched every time the screen gains focus) and derives the card state.
 * The mentor plan card shares one controller with this card, so its "Start here" and the checklist's
 * NEXT step always agree.
 */
export function useMentorSetupChecklist({ mentorPlan = false, enabled = true }: { mentorPlan?: boolean; enabled?: boolean } = {}): MentorChecklistController {
  const { billingStatus, user } = useAuth();
  const entitled = hasEntitlement(billingStatus, 'mentor_engagements', billingStatus?.plan_tier === 'tier3');
  const [checklist, setChecklist] = useState<MentorSetupChecklist | null>(null);
  const [loading, setLoading] = useState(entitled && enabled);
  const [failed, setFailed] = useState(false);
  const loadId = useRef(0);

  const load = useCallback(async () => {
    if (!entitled || !enabled) return;
    const id = ++loadId.current;
    setLoading(true);
    try {
      const next = await MentorEngagementsAPI.getSetupChecklist();
      if (id !== loadId.current) return;
      setChecklist(next);
      setFailed(false);
    } catch {
      if (id === loadId.current) setFailed(true);
    } finally {
      if (id === loadId.current) setLoading(false);
    }
  }, [entitled, enabled]);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  const state = checklistCardState({
    entitled,
    mentorPlan,
    loading,
    failed,
    checklist,
    hasSchool: user?.school_id != null,
  });
  return { state, load };
}

interface Props {
  surface: Surface;
  /** The card stands in for the mentor workspace plan (dashboard/progress). Mentees renders it only when it applies. */
  mentorPlan?: boolean;
  testID?: string;
  /** Shared with the mentor plan card; without it the card loads the checklist itself. */
  controller?: MentorChecklistController;
}

export function MentorSetupChecklistCard({ surface, mentorPlan = false, testID = 'mentor_setup_checklist', controller }: Props) {
  const router = useRouter();
  const { t } = useTranslation();
  const own = useMentorSetupChecklist({ mentorPlan, enabled: !controller });
  const { state, load } = controller ?? own;
  const [upgradeVisible, setUpgradeVisible] = useState(false);
  const viewTracked = useRef(false);

  useEffect(() => {
    if (viewTracked.current || state.kind === 'hidden' || state.kind === 'loading') return;
    viewTracked.current = true;
    trackAuthEvent('mentor_checklist_viewed', {
      surface,
      state: state.kind,
      completed_steps: 'checklist' in state ? state.checklist.completed : null,
      total_steps: 'checklist' in state ? state.checklist.total : null,
    });
  }, [state, surface]);

  if (state.kind === 'hidden') return null;

  const open = (href: ChecklistHref, step: string) => {
    trackAuthEvent('mentor_checklist_cta_clicked', { surface, step });
    router.push(href as never);
  };

  const header = (completed: number, total: number) => (
    <View style={styles.header}>
      <View style={styles.titleRow}>
        <View style={styles.icon}>
          <MaterialIcons name="checklist" size={21} color={reviewDesk.accent} />
        </View>
        <View style={styles.headerCopy}>
          <Text selectable style={styles.title}>{t('mentorChecklist.title')}</Text>
          <Text selectable style={styles.subtitle}>{t('mentorChecklist.subtitle')}</Text>
        </View>
      </View>
      <Text testID={`${testID}_progress`} style={styles.count}>
        {t('mentorChecklist.progress', { completed, total })}
      </Text>
    </View>
  );

  if (state.kind === 'loading' || state.kind === 'error') {
    return (
      <View testID={testID} style={styles.card}>
        <View style={styles.titleRow}>
          <View style={styles.icon}><MaterialIcons name="checklist" size={21} color={reviewDesk.accent} /></View>
          <Text selectable style={[styles.title, styles.headerCopy]}>{t('mentorChecklist.title')}</Text>
        </View>
        {state.kind === 'loading' ? (
          <ActivityIndicator color={reviewDesk.accent} />
        ) : (
          <View style={styles.rowWrap}>
            <Text accessibilityRole="alert" style={styles.body}>{t('mentorChecklist.loadError')}</Text>
            <CtaButton testID={`${testID}_retry`} label={t('mentorChecklist.retry')} icon="refresh" variant="secondary" onPress={() => void load()} />
          </View>
        )}
      </View>
    );
  }

  const { checklist: data } = state;
  const ratio = data.total > 0 ? data.completed / data.total : 0;

  return (
    <View testID={testID} style={styles.card}>
      {header(data.completed, data.total)}
      <View
        style={styles.track}
        accessibilityRole="progressbar"
        accessibilityValue={{ min: 0, max: data.total, now: data.completed }}
      >
        <View style={[styles.fill, { width: `${Math.round(ratio * 100)}%` }]} />
      </View>

      {state.kind === 'complete' ? (
        <View testID={`${testID}_complete`} style={styles.complete}>
          <MaterialIcons name="celebration" size={22} color={reviewDesk.success} />
          <View style={styles.headerCopy}>
            <Text selectable style={styles.itemTitle}>{t('mentorChecklist.completeTitle')}</Text>
            <Text selectable style={styles.body}>{t('mentorChecklist.completeBody')}</Text>
          </View>
          <CtaButton
            testID={`${testID}_complete_cta`}
            label={t('mentorChecklist.completeCta')}
            icon="person-add-alt"
            onPress={() => open(checklistCompleteHref(), 'complete')}
          />
        </View>
      ) : null}

      <View style={styles.list}>
        {data.steps.map((step, index) => renderStep({
          step,
          index,
          isNext: state.kind === 'progress' && state.next.key === step.key,
          locked: state.kind === 'locked',
          rowTestID: `${testID}_step_${step.key}`,
          onOpen: () => open(checklistStepHref(step), step.key),
        }))}
      </View>

      {state.kind === 'locked' ? (
        <View testID={`${testID}_locked`} style={styles.rowWrap}>
          <MaterialIcons name="lock-outline" size={18} color={reviewDesk.accent} />
          <Text selectable style={[styles.body, styles.headerCopy]}>{t('mentorChecklist.lockedBody')}</Text>
          <CtaButton
            testID={`${testID}_upgrade`}
            label={t('mentorChecklist.lockedCta')}
            icon="arrow-forward"
            onPress={() => {
              trackAuthEvent('mentor_checklist_cta_clicked', { surface, step: 'upgrade' });
              setUpgradeVisible(true);
            }}
          />
          <UpgradeModal
            visible={upgradeVisible}
            onClose={() => setUpgradeVisible(false)}
            requiredTier="tier3"
            featureKey="mentor_engagements"
          />
        </View>
      ) : null}
    </View>
  );

  function renderStep({ step, index, isNext, locked, rowTestID, onOpen }: {
    step: MentorChecklistStep;
    index: number;
    isNext: boolean;
    locked: boolean;
    rowTestID: string;
    onOpen: () => void;
  }) {
    const copy = `mentorChecklist.steps.${step.key}`;
    return (
      <View
        key={step.key}
        testID={rowTestID}
        accessibilityLabel={`${t(`${copy}.title`)}: ${step.done ? t('mentorChecklist.done') : isNext ? t('mentorChecklist.next') : ''}`}
        style={[styles.step, isNext && styles.stepNext]}
      >
        <View style={[styles.marker, step.done && styles.markerDone]}>
          {step.done
            ? <MaterialIcons name="check" size={16} color="#FFFFFF" />
            : <Text style={styles.markerText}>{index + 1}</Text>}
        </View>
        <View style={styles.headerCopy}>
          <View style={styles.rowWrap}>
            <MaterialIcons name={CHECKLIST_STEP_ICONS[step.key] as never} size={16} color={step.done ? reviewDesk.muted : reviewDesk.inkSoft} />
            <Text selectable style={[styles.itemTitle, step.done && styles.itemTitleDone]}>{t(`${copy}.title`)}</Text>
            {isNext ? <Text style={styles.nextChip}>{t('mentorChecklist.next')}</Text> : null}
          </View>
          {step.done ? (
            <Text selectable testID={`${rowTestID}_done`} style={styles.success}>{t(`${copy}.done`)}</Text>
          ) : (
            <>
              <Text selectable style={styles.body}>{t(`${copy}.why`)}</Text>
              <Text selectable style={styles.marker2}>
                {t('mentorChecklist.successLabel')}: {t(`${copy}.done`)}
              </Text>
            </>
          )}
          {isNext && !locked ? (
            <View style={styles.ctaRow}>
              <CtaButton testID={`${rowTestID}_cta`} label={t('mentorChecklist.doItNow')} icon="arrow-forward" onPress={onOpen} />
            </View>
          ) : null}
        </View>
      </View>
    );
  }
}

function CtaButton({ label, onPress, icon, testID, variant = 'primary' }: {
  label: string;
  onPress: () => void;
  icon: keyof typeof MaterialIcons.glyphMap;
  testID?: string;
  variant?: 'primary' | 'secondary';
}) {
  const primary = variant === 'primary';
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        styles.cta,
        primary ? styles.ctaPrimary : styles.ctaSecondary,
        pressed && { opacity: 0.85 },
      ]}
    >
      <Text style={[styles.ctaText, { color: primary ? '#FFFFFF' : reviewDesk.accent }]}>{label}</Text>
      <MaterialIcons name={icon} size={16} color={primary ? '#FFFFFF' : reviewDesk.accent} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: { width: '100%', backgroundColor: reviewDesk.card, borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, padding: 18, gap: 14 },
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  titleRow: { flex: 1, minWidth: 220, flexDirection: 'row', alignItems: 'flex-start', gap: 11 },
  icon: { width: 38, height: 38, borderRadius: 11, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.accentSoft },
  headerCopy: { flex: 1, minWidth: 0, gap: 3 },
  title: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800' },
  subtitle: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 18 },
  count: { color: reviewDesk.accent, fontSize: 13, fontWeight: '800', fontVariant: ['tabular-nums'], backgroundColor: reviewDesk.accentSoft, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 5 },
  track: { height: 6, borderRadius: 999, backgroundColor: reviewDesk.cardMuted, overflow: 'hidden' },
  fill: { height: 6, borderRadius: 999, backgroundColor: reviewDesk.accent },
  list: { gap: 8 },
  step: { flexDirection: 'row', alignItems: 'flex-start', gap: 11, padding: 10, borderRadius: 12 },
  stepNext: { backgroundColor: reviewDesk.accentWash, borderWidth: 1, borderColor: reviewDesk.accentSoft },
  marker: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.accentSoft },
  markerDone: { backgroundColor: reviewDesk.success },
  markerText: { color: reviewDesk.accent, fontWeight: '800', fontVariant: ['tabular-nums'] },
  rowWrap: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  itemTitle: { color: reviewDesk.ink, fontSize: 15, fontWeight: '700', flexShrink: 1 },
  itemTitleDone: { color: reviewDesk.muted },
  nextChip: { color: reviewDesk.accent, fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.4 },
  body: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19 },
  marker2: { color: reviewDesk.muted, fontSize: 12, lineHeight: 17 },
  success: { color: reviewDesk.success, fontSize: 12, lineHeight: 18, fontWeight: '600' },
  ctaRow: { flexDirection: 'row', marginTop: 6 },
  cta: { flexDirection: 'row', alignItems: 'center', gap: 6, minHeight: 40, paddingHorizontal: 14, borderRadius: 12, borderWidth: 1 },
  ctaPrimary: { backgroundColor: reviewDesk.accent, borderColor: reviewDesk.accent },
  ctaSecondary: { backgroundColor: reviewDesk.card, borderColor: reviewDesk.line },
  ctaText: { fontSize: 13, fontWeight: '700' },
  complete: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: 12, borderRadius: 12, backgroundColor: reviewDesk.successSoft },
});

export default MentorSetupChecklistCard;
